import React from "react";

import { useTable } from "@refinedev/react-table";
import { useQuery } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";

import { CitySearchSelect } from "@/components/fields/city-search-select";
import { DataTable } from "@/components/refine-ui/data-table/data-table";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import {
  AREA_AUDIT_KIND_HINT,
  AREA_AUDIT_SEVERITY_OPTIONS,
  AREA_AUDIT_STATUS_OPTIONS,
  isAreaAuditKind,
  isAreaAuditNotDeployed,
  isAreaAuditSeverity,
  isAreaAuditStatus,
} from "@/lib/tina/area-audit";
import { AreaDismissFindingDialog } from "@/pages/tina/audits/area/components/dismiss-finding-dialog";
import { AreaKindCountStrip } from "@/pages/tina/audits/area/components/kind-count-strip";
import { useAreaAuditCities } from "@/pages/tina/audits/area/hooks/use-area-audit-cities";
import { useAreaAuditColumns } from "@/pages/tina/audits/area/hooks/use-area-audit-columns";
import {
  AREA_AUDIT_QUERY_ROOT,
  useAreaAuditRepository,
} from "@/pages/tina/audits/area/hooks/use-area-audit-repository";
import {
  type AreaAuditFilterState,
  buildAreaAuditFilters,
} from "@/pages/tina/audits/area/lib/area-audit-filters";
import { usePersistedState } from "@/pages/tina/hooks/use-persisted-state";
import { parseCityKey, cityKey as toCityKey } from "@/types/area";
import type { AreaAuditFindingRow, VenueAuditRunResponse } from "@/types/tina";

const ANY_SEVERITY = "__any_severity__";

/**
 * A failed read is not worth three retries when the route is simply missing: the 404
 * says "not deployed", and retrying only delays the tab saying so.
 */
const retryUnlessMissing = (failureCount: number, error: unknown): boolean =>
  !isAreaAuditNotDeployed(error) && failureCount < 2;

/** Why the tab has no table: the route is missing, or it failed. */
const Unavailable = ({ error }: { error: unknown }) => {
  const missing = isAreaAuditNotDeployed(error);
  return (
    <div
      role="status"
      className="flex flex-col gap-1 rounded-md border border-dashed p-6 text-sm"
    >
      <strong className="font-medium">
        {missing
          ? "The area audit is not available yet."
          : "The area audit is not available right now."}
      </strong>
      <span className="text-muted-foreground">
        {missing
          ? "Tina has not shipped its area audit yet, so there are no findings to show. They appear here after the first nightly run once it has."
          : `Tina could not return the findings: ${toHumanErrorMessage(error)}`}
      </span>
    </div>
  );
};

/**
 * The Area tab of `/tina/audits` — the nightly area step's review queue.
 *
 * **What it is for.** The venue audit job's area step keeps Mood's areas in shape every
 * night, and files what it cannot settle on its own to `area_audit_findings`: venues that
 * look mislabelled or sit in no area, areas with no part, parts too small, cities held
 * back, empty areas and the areas Tina made that nobody has checked. This tab replaced the
 * Issues tab of `/areas` on 2026-11-01; the Discord report the step used to send is gone.
 *
 * **Dismissal is the only action.** Fixing a finding means editing the area or the venue
 * each row links to; dismissing records that a curator accepted the finding, until its
 * evidence changes.
 *
 * **Before Tina ships the routes** `GET /area-audit/findings` 404s. The tab then shows a
 * "not available yet" panel in place of the count strip and the table — never an empty
 * table, which would read as "nothing to fix".
 *
 * All narrowing is server-side: `status`, `kind`, `severity` and `city` go in as
 * `permanent` filters and `total` is counted over the same WHERE the page is.
 */
export const AreaAuditTab = () => {
  const repo = useAreaAuditRepository();
  const cities = useAreaAuditCities();

  const [persistedStatus, setStatus] = usePersistedState<string | null>(
    "tina-area-audit:status",
    "open",
  );
  const [persistedKind, setKind] = usePersistedState<string | null>(
    "tina-area-audit:kind",
    null,
  );
  const [persistedSeverity, setSeverity] = usePersistedState<string | null>(
    "tina-area-audit:severity",
    null,
  );
  // `City|CC`, so the picker can show which of two same-named cities was picked; only the
  // city name goes on the wire.
  const [persistedCityKey, setCityKey] = usePersistedState<string | null>(
    "tina-area-audit:city",
    null,
  );
  // Narrowed on read rather than trusted: localStorage can hold a value an older build
  // wrote, and the route types these params as Literals — an unknown value is a 422.
  const status = isAreaAuditStatus(persistedStatus) ? persistedStatus : "open";
  const kind = isAreaAuditKind(persistedKind) ? persistedKind : null;
  const severity = isAreaAuditSeverity(persistedSeverity)
    ? persistedSeverity
    : null;
  const picked =
    typeof persistedCityKey === "string" && persistedCityKey
      ? parseCityKey(persistedCityKey)
      : null;
  const city = picked?.city || null;

  const [pendingDismissal, setPendingDismissal] =
    React.useState<AreaAuditFindingRow | null>(null);
  const [dismissingId, setDismissingId] = React.useState<string | null>(null);
  const [runOutcome, setRunOutcome] =
    React.useState<VenueAuditRunResponse | null>(null);
  const [running, setRunning] = React.useState(false);

  const onDismiss = React.useCallback((row: AreaAuditFindingRow) => {
    setPendingDismissal(row);
  }, []);
  const columns = useAreaAuditColumns({ onDismiss, dismissingId });

  const filterState: AreaAuditFilterState = { status, kind, severity, city };
  const table = useTable<AreaAuditFindingRow>({
    columns,
    refineCoreProps: {
      // Resolved by `RESOURCE_PATHS` to `/area-audit/findings`.
      resource: "tina-area-audit",
      pagination: { pageSize: 50 },
      filters: { permanent: buildAreaAuditFilters(filterState) },
      // The tab renders its own failure state; a toast on every visit before Tina ships
      // the routes would only be noise.
      errorNotification: false,
      queryOptions: { retry: retryUnlessMissing },
    },
  });

  const setCurrentPage = table.refineCore.setCurrentPage;
  const tableQuery = table.refineCore.tableQuery;
  const total = tableQuery.data?.total;
  const tableError = tableQuery.isError ? tableQuery.error : null;
  const notDeployed = tableError !== null && isAreaAuditNotDeployed(tableError);

  // Back to page 1 whenever the narrowing changes, so a pick never strands the table on
  // an out-of-range page of a smaller result.
  const scope = `${status}|${kind}|${severity}|${city}`;
  const previousScope = React.useRef(scope);
  React.useEffect(() => {
    if (previousScope.current === scope) return;
    previousScope.current = scope;
    setCurrentPage(1);
  }, [scope, setCurrentPage]);

  const countsQuery = useQuery({
    queryKey: [AREA_AUDIT_QUERY_ROOT, "counts", status, severity, city],
    queryFn: () => repo.countsByKind({ status, severity, city }),
    retry: retryUnlessMissing,
    enabled: !notDeployed,
  });

  async function confirmDismissal(note: string): Promise<void> {
    const finding = pendingDismissal;
    if (!finding) return;
    setDismissingId(finding.id);
    try {
      const result = await repo.dismiss(finding.id, note.trim() || null);
      // `null` is the "identity has not loaded" refusal, already surfaced; keeping the
      // dialog open lets the curator retry after a reload.
      if (result) setPendingDismissal(null);
    } catch {
      // The repository surfaced it; the dialog stays open so nothing is lost.
    } finally {
      setDismissingId(null);
    }
  }

  async function onRunNow(): Promise<void> {
    if (running) return;
    setRunning(true);
    try {
      setRunOutcome(await repo.runNow());
      await countsQuery.refetch();
    } catch {
      // Surfaced by the repository.
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">
            Area audit
            {typeof total === "number" ? (
              <span className="ml-2 font-mono text-base tabular-nums text-muted-foreground">
                {total}
              </span>
            ) : null}
          </h2>
          <p className="max-w-3xl text-sm text-muted-foreground">
            What the nightly area step could not settle on its own. A dismissed
            finding comes back only if its evidence changes.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={status} onValueChange={(value) => setStatus(value)}>
            <SelectTrigger className="h-9 w-[150px]" aria-label="Status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AREA_AUDIT_STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={severity ?? ANY_SEVERITY}
            onValueChange={(value) =>
              setSeverity(value === ANY_SEVERITY ? null : value)
            }
          >
            <SelectTrigger className="h-9 w-[160px]" aria-label="Severity">
              <SelectValue placeholder="Any severity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_SEVERITY}>Any severity</SelectItem>
              {AREA_AUDIT_SEVERITY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <CitySearchSelect
            options={cities}
            city={picked?.city ?? null}
            country={picked?.countryCode ?? null}
            onChange={(pick) => setCityKey(pick ? toCityKey(pick) : null)}
          />
          {notDeployed ? null : (
            <Button
              variant="outline"
              className="h-9"
              onClick={() => void onRunNow()}
              disabled={running}
              title="Starts the venue audit job, whose nightly area step files these findings. The job also writes to prod: area shapes, venue counts and parts of new areas. It takes a while, and this page does not update on its own — reload it when the run is done."
            >
              {running ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Run venue + area audit now
            </Button>
          )}
        </div>
      </div>

      {tableError !== null ? (
        <Unavailable error={tableError} />
      ) : (
        <>
          <AreaKindCountStrip
            counts={countsQuery.data}
            isLoading={countsQuery.isLoading}
            selected={kind}
            onSelect={(next) => setKind(next)}
          />

          <p className="max-w-4xl text-xs text-muted-foreground">
            {kind
              ? AREA_AUDIT_KIND_HINT[kind]
              : "Pick a kind to work one question at a time. High severity is a city held back; medium is a venue or an area left out of the city's layout."}
          </p>

          {runOutcome ? (
            <div
              className={
                runOutcome.kind === "dispatched"
                  ? "rounded-md border bg-muted/40 p-3 text-sm"
                  : "rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm"
              }
            >
              {runOutcome.kind === "dispatched" ? (
                <span>
                  <strong className="font-medium">Audit started.</strong> The
                  area step runs after the venue checks, so it takes a while.
                  Nothing on this page updates on its own — reload it when the
                  run is done.
                </span>
              ) : (
                <span>
                  <strong className="font-medium">
                    {runOutcome.kind === "noop"
                      ? "Nothing started."
                      : "The audit could not be started."}
                  </strong>{" "}
                  {runOutcome.kind === "noop"
                    ? "The audit job is not configured on this environment."
                    : (runOutcome.error_message ??
                      "Cloud Run refused and gave no reason.")}
                </span>
              )}
            </div>
          ) : null}

          <DataTable table={table} />
        </>
      )}

      <AreaDismissFindingDialog
        finding={pendingDismissal}
        pending={dismissingId !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDismissal(null);
        }}
        onConfirm={(note) => void confirmDismissal(note)}
      />
    </div>
  );
};
